// Whisper — the Discord webhook pipe into Agoracle Command Center
// (#the-whisper-log channel). Both Whispers (Feedback) and Alarms
// (ErrorBoundary) land here, colour-coded by level.
//
// NOTE: This URL ships inside the client bundle. Anyone inspecting the
// deployed JS can read it, so treat it as a "public, easily-rotated"
// endpoint rather than a secret. If abuse appears, rotate the webhook
// in Discord and update REACT_APP_DISCORD_WHISPER_WEBHOOK or the
// fallback below.

const DEFAULT_WEBHOOK =
  'https://discord.com/api/webhooks/1495667418997985381/nhhbR0uRY5We6X84J4eiyy3pxD8UOm-cV2Hae3Fen293dSqQs0buk2j8KYCzo-Q0zNUD';

const WEBHOOK_URL =
  process.env.REACT_APP_DISCORD_WHISPER_WEBHOOK || DEFAULT_WEBHOOK;

export type WhisperLevel = 'info' | 'signal' | 'alarm';

// Discord embed colours (decimal ints, not hex strings)
const COLOR: Record<WhisperLevel, number> = {
  info:   0x64748b, // slate
  signal: 0x7c3aed, // purple
  alarm:  0xdc2626, // red — technical alerts
};

export interface WhisperPayload {
  title:    string;
  message:  string;
  level?:   WhisperLevel;
  author?:  string;
  fields?:  Array<{ name: string; value: string; inline?: boolean }>;
}

function clip(s: string, n: number): string {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export async function sendWhisper(p: WhisperPayload): Promise<boolean> {
  if (!WEBHOOK_URL) return false;
  const level = p.level ?? 'info';

  const embed: Record<string, unknown> = {
    title:       clip(p.title, 256),
    description: clip(p.message, 4000),
    color:       COLOR[level],
    timestamp:   new Date().toISOString(),
    footer:      { text: `Agoracle · Architect: Dromide · ${level.toUpperCase()}` },
  };
  if (p.fields && p.fields.length) {
    embed.fields = p.fields.slice(0, 10).map(f => ({
      name:   clip(f.name,  256),
      value:  clip(f.value, 1024),
      inline: !!f.inline,
    }));
  }
  const body = {
    username: p.author ? clip(`Agoracle · ${p.author}`, 80) : 'Agoracle',
    embeds:   [embed],
  };

  try {
    const res = await fetch(WEBHOOK_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(body),
    });
    return res.ok;
  } catch {
    return false;
  }
}

export function sendAlarm(err: unknown, context?: string): Promise<boolean> {
  const e = err as Error;
  const stack  = (e && e.stack)   ? e.stack.split('\n').slice(0, 12).join('\n') : '';
  const name   = (e && e.name)    || 'Error';
  const detail = (e && e.message) || String(err);

  return sendWhisper({
    title:   `ALARM · ${name}`,
    message: detail,
    level:   'alarm',
    fields: [
      ...(context ? [{ name: 'Context', value: context, inline: false }] : []),
      ...(stack   ? [{ name: 'Stack',   value: '```\n' + stack + '\n```', inline: false }] : []),
      { name: 'URL',       value: typeof window !== 'undefined' ? window.location.href : 'n/a', inline: true },
      { name: 'UserAgent', value: typeof navigator !== 'undefined' ? clip(navigator.userAgent, 120) : 'n/a', inline: true },
    ],
  });
}
